import { TelegramSDK } from '../utils/telegram'
import type {
  AdminDebugScenario,
  ClientDisconnectUploadPayload,
  ClientPerfUploadPayload,
} from '@star-snake/shared'
import { NETCODE_V4_PROTOCOL_VERSION } from '@star-snake/shared'

const API_BASE = import.meta.env.PROD
  ? `${window.location.origin}/api`
  : '/api'

const configuredWsEndpoint = import.meta.env.VITE_GAME_WS_ENDPOINT?.trim()
const isHttps = window.location.protocol === 'https:'
const defaultWsEndpoint = (import.meta.env.PROD || isHttps)
  ? `wss://${window.location.host}/ws`
  : `ws://${window.location.hostname}:2568/ws`
export function resolveWsEndpoints(
  preferredEndpoint: string | undefined | null,
  sameOriginEndpoint: string,
): string[] {
  return Array.from(new Set([
    preferredEndpoint && preferredEndpoint.trim().length > 0 ? preferredEndpoint.trim() : null,
    sameOriginEndpoint,
  ].filter((value): value is string => Boolean(value))))
}

const WS_ENDPOINTS = resolveWsEndpoints(configuredWsEndpoint, defaultWsEndpoint)
const configuredProtocolVersion = Number.parseInt(
  import.meta.env.VITE_NETCODE_PROTOCOL_VERSION?.trim() ?? '',
  10,
)
const WS_PROTOCOL_VERSION = Number.isFinite(configuredProtocolVersion) && configuredProtocolVersion > 0
  ? configuredProtocolVersion
  : NETCODE_V4_PROTOCOL_VERSION
const MAX_PENDING_BINARY_MESSAGES = 120

/**
 * Wrapper around native WebSocket that provides:
 * - Binary message routing
 * - Clean binary send/leave API
 */
export class GameConnection {
  private ws: WebSocket
  private binaryListeners: Set<(data: ArrayBuffer) => void> = new Set()
  private pendingBinaryMessages: ArrayBuffer[] = []
  private pendingBinaryStart = 0
  private droppedPendingBinaryMessages = 0
  private leaveListeners: Set<(event: { code: number; reason: string }) => void> = new Set()
  private closeEvent: { code: number; reason: string } | null = null
  private _sessionId: string = ''  // Set from server snake assignment
  private readonly _protocolVersion: number

  constructor(ws: WebSocket, protocolVersion: number) {
    this.ws = ws
    this._protocolVersion = protocolVersion

    ws.binaryType = 'arraybuffer'

    ws.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        if (this.binaryListeners.size === 0) {
          if (this.pendingBinaryMessages.length - this.pendingBinaryStart >= MAX_PENDING_BINARY_MESSAGES) {
            this.pendingBinaryStart++
            this.droppedPendingBinaryMessages++
          }
          this.pendingBinaryMessages.push(event.data)
          this.compactPendingBinaryQueue()
          return
        }
        for (const cb of this.binaryListeners) cb(event.data)
      }
    }

    ws.onclose = (event) => {
      this.closeEvent = { code: event.code, reason: event.reason }
      for (const cb of this.leaveListeners) cb(this.closeEvent)
    }
  }

  get sessionId(): string {
    return this._sessionId
  }

  get protocolVersion(): number {
    return this._protocolVersion
  }

  setSessionId(id: string): void {
    this._sessionId = id
  }

  onBinary(callback: (data: ArrayBuffer) => void): () => void {
    this.binaryListeners.add(callback)
    if (this.pendingBinaryMessages.length > this.pendingBinaryStart) {
      for (let i = this.pendingBinaryStart; i < this.pendingBinaryMessages.length; i++) {
        callback(this.pendingBinaryMessages[i])
      }
      this.pendingBinaryMessages.length = 0
      this.pendingBinaryStart = 0
    }
    return () => {
      this.binaryListeners.delete(callback)
    }
  }

  onLeave(callback: (event: { code: number; reason: string }) => void): () => void {
    this.leaveListeners.add(callback)
    if (this.closeEvent) callback(this.closeEvent)
    return () => {
      this.leaveListeners.delete(callback)
    }
  }

  sendBinary(data: ArrayBuffer): void {
    if (this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data)
    }
  }

  async leave(): Promise<void> {
    this.ws.close(1000)
  }

  get readyState(): number {
    return this.ws.readyState
  }

  getPendingBinaryStats(): { queued: number; dropped: number; bufferedAmount: number } {
    return {
      queued: this.pendingBinaryMessages.length - this.pendingBinaryStart,
      dropped: this.droppedPendingBinaryMessages,
      bufferedAmount: this.ws.bufferedAmount,
    }
  }

  private compactPendingBinaryQueue(): void {
    if (this.pendingBinaryStart <= 0) return
    if (this.pendingBinaryStart < 64 && this.pendingBinaryStart * 2 < this.pendingBinaryMessages.length) return
    this.pendingBinaryMessages = this.pendingBinaryMessages.slice(this.pendingBinaryStart)
    this.pendingBinaryStart = 0
  }
}

class JoinGameError extends Error {
  readonly debugMessage: string

  constructor(publicMessage: string, debugMessage: string) {
    super(publicMessage)
    this.name = 'JoinGameError'
    this.debugMessage = debugMessage
  }
}

function sanitizeWsUrl(rawUrl: string): string {
  try {
    const parsed = new URL(rawUrl)
    if (parsed.searchParams.has('token')) {
      parsed.searchParams.set('token', '[redacted]')
    }
    return parsed.toString()
  } catch {
    return rawUrl.replace(/([?&]token=)[^&]+/i, '$1[redacted]')
  }
}

function buildJoinGameError(
  publicMessage: string,
  debugMessage: string,
): JoinGameError {
  return new JoinGameError(publicMessage, debugMessage)
}

function buildJoinGameCloseError(
  event: { code: number; reason: string },
  sanitizedUrl: string,
): JoinGameError {
  const trimmedReason = event.reason.trim()
  if (trimmedReason.length > 0) {
    return buildJoinGameError(
      trimmedReason,
      `WS closed code=${event.code} reason=${trimmedReason} | url=${sanitizedUrl}`,
    )
  }

  if (event.code === 1006) {
    return buildJoinGameError(
      'Connection was interrupted while starting the game. Please try again.',
      `WS closed code=1006 reason=none | url=${sanitizedUrl}`,
    )
  }

  return buildJoinGameError(
    `Connection closed while starting the game (code ${event.code}). Please try again.`,
    `WS closed code=${event.code} reason=none | url=${sanitizedUrl}`,
  )
}

function shouldRetryJoinOnAlternateEndpoint(error: JoinGameError): boolean {
  return error.debugMessage.startsWith('WS construct:')
    || error.debugMessage.startsWith('WS error')
    || error.debugMessage.startsWith('Timeout')
    || error.debugMessage.startsWith('WS closed code=1006')
}

export interface ClientErrorReportPayload {
  clientSessionId: string
  occurredAtMs: number
  screenMessage: string
  debugMessage: string | null
  roomName: string | null
  appState: string
  pageUrl: string
  userAgent: string
  buildLabel: string | null
  account: {
    telegramId: string | null
    displayName: string | null
    isAdmin: boolean
    balance: number
    freeGamesLeft: number
  }
  snapshot: {
    updatedAtMs: number
    fps: number
    avgFrameDtMs: number
    maxFrameDtMs: number
    avgGameLoopMs: number
    inputAckMs: number
    rttMs: number
    pendingInputAckCount: number
    wsBufferedAmount: number
    pendingBinaryCount: number
    droppedPendingBinaryCount: number
    gcPauseCount: number
    heapUsedMb: number | null
    longStallCount: number
    worstLongStallMs: number
  } | null
  telemetry: {
    attemptedCount: number
    succeededCount: number
    failedCount: number
    consecutiveFailureCount: number
    lastAttemptAtMs: number | null
    lastSuccessAtMs: number | null
    lastFailureAtMs: number | null
    lastHttpStatus: number | null
    lastError: string | null
  } | null
}

export class NetworkManager {
  private static instance: NetworkManager
  private static readonly TOKEN_STORAGE_KEY = 'token'

  private token: string | null = null
  private connection: GameConnection | null = null
  private readonly clientSessionId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `client-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`
  private _isAdmin = false
  private _balance = 0
  private _freeGamesLeft = 0
  private _username: string | null = null
  private _firstName: string | null = null
  private _telegramId: string | null = null

  private constructor() {}

  static getInstance(): NetworkManager {
    if (!NetworkManager.instance) {
      NetworkManager.instance = new NetworkManager()
    }
    return NetworkManager.instance
  }

  get isAdmin(): boolean { return this._isAdmin }
  get clientDebugSessionId(): string { return this.clientSessionId }
  get balance(): number { return this._balance }
  get freeGamesLeft(): number { return this._freeGamesLeft }
  get defaultDisplayName(): string { return this._username ?? this._firstName ?? '' }
  get telegramId(): string | null { return this._telegramId }

  private persistToken(token: string): void {
    this.token = token
    try {
      localStorage.setItem(NetworkManager.TOKEN_STORAGE_KEY, token)
    } catch {
      // Storage persistence is optional; auth should still work without it.
    }
  }

  private async parseUploadFailure(response: Response): Promise<string> {
    try {
      const data = await response.json() as { error?: string; details?: Array<{ message?: string }> }
      const detail = data.details?.[0]?.message
      return [data.error, detail].filter(Boolean).join(': ') || `HTTP ${response.status}`
    } catch {
      return `HTTP ${response.status}`
    }
  }

  async authenticate(): Promise<void> {
    const initData = await TelegramSDK.waitForInitData()

    if (initData) {
      const res = await fetch(`${API_BASE}/auth/telegram`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ initData }),
      })
      if (!res.ok) throw new Error('Authentication failed')
      const data = await res.json()
      this.persistToken(data.data.token)
      this._isAdmin = data.data.user?.isAdmin ?? false
      this._balance = data.data.user?.balance ?? 0
      this._freeGamesLeft = data.data.user?.freeGamesLeft ?? 0
      this._username = data.data.user?.username ?? null
      this._firstName = data.data.user?.firstName ?? null
      this._telegramId = data.data.user?.telegramId != null ? String(data.data.user.telegramId) : null
      return
    }

    if (import.meta.env.PROD || TelegramSDK.hasWebAppContext) {
      throw new Error('Missing Telegram launch data. Reopen the game from Telegram Mini App.')
    }

    // Dev auth fallback
    const res = await fetch(`${API_BASE}/auth/dev`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })
    if (!res.ok) throw new Error('Authentication failed — launch from Telegram Mini App')
    const data = await res.json()
    this.persistToken(data.data.token)
    this._isAdmin = data.data.user?.isAdmin ?? false
    this._balance = data.data.user?.balance ?? 0
    this._freeGamesLeft = data.data.user?.freeGamesLeft ?? 0
    this._username = data.data.user?.username ?? null
    this._firstName = data.data.user?.firstName ?? null
    this._telegramId = data.data.user?.telegramId != null ? String(data.data.user.telegramId) : null
  }

  async fetchBalance(): Promise<void> {
    if (!this.token) return
    try {
      const res = await fetch(`${API_BASE}/profile/stats`, {
        headers: { Authorization: `Bearer ${this.token}` },
      })
      if (!res.ok) return
      const data = await res.json()
      this._balance = data.data?.balance ?? this._balance
      this._freeGamesLeft = data.data?.freeGamesLeft ?? this._freeGamesLeft
      this._isAdmin = data.data?.isAdmin ?? this._isAdmin
      this._username = data.data?.username ?? this._username
      this._firstName = data.data?.firstName ?? this._firstName
      this._telegramId = data.data?.telegramId != null ? String(data.data.telegramId) : this._telegramId
    } catch { /* ignore */ }
  }

  async uploadClientPerf(payload: ClientPerfUploadPayload): Promise<{ status: number } | null> {
    if (!this.token || !this._isAdmin) return null
    const response = await fetch(`${API_BASE}/profile/client-perf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.token}`,
      },
      body: JSON.stringify(payload),
    })
    if (!response.ok) {
      const error = await this.parseUploadFailure(response)
      throw new Error(error)
    }
    return { status: response.status }
  }

  async uploadClientDisconnect(payload: ClientDisconnectUploadPayload): Promise<{ status: number } | null> {
    if (!this.token || !this._isAdmin) return null
    const response = await fetch(`${API_BASE}/profile/client-disconnect`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.token}`,
      },
      body: JSON.stringify(payload),
    })
    if (!response.ok) {
      const error = await this.parseUploadFailure(response)
      throw new Error(error)
    }
    return { status: response.status }
  }

  async reportClientError(payload: ClientErrorReportPayload): Promise<{ status: number } | null> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`
    }
    const response = await fetch(`${API_BASE}/profile/client-error-report`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    })
    if (!response.ok) {
      const error = await this.parseUploadFailure(response)
      throw new Error(error)
    }
    return { status: response.status }
  }

  async runAdminRoomDebugScenario(
    roomName: string,
    scenario: AdminDebugScenario,
    options: { sparks?: number } = {},
  ): Promise<void> {
    if (!this.token || !this._isAdmin) {
      throw new Error('Admin access required')
    }
    const resp = await fetch(`${API_BASE}/admin/rooms/${encodeURIComponent(roomName)}/debug/scenario`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.token}`,
      },
      body: JSON.stringify({
        scenario,
        ...options,
      }),
    })
    if (!resp.ok) {
      const data = await resp.json().catch(() => null)
      throw new Error(data?.error ?? 'Failed to run debug scenario')
    }
  }

  /**
   * Join game room via WebSocket.
   * Token and stake are passed as query parameters.
   * Returns GameConnection wrapper.
   */
  async joinGame(roomName: string, stake: number, playerName?: string): Promise<GameConnection> {
    if (!this.token) throw new Error('Not authenticated')
    const token = this.token
    const attemptJoin = async (endpoint: string, timeoutMs: number): Promise<GameConnection> => {
      let url = `${endpoint}?room=${encodeURIComponent(roomName)}&token=${encodeURIComponent(token)}&stake=${stake}&protocolVersion=${WS_PROTOCOL_VERSION}`
      if (playerName) url += `&name=${encodeURIComponent(playerName)}`
      const sanitizedUrl = sanitizeWsUrl(url)

      return new Promise<GameConnection>((resolve, reject) => {
        let settled = false
        const settle = (fn: () => void) => {
          if (!settled) { settled = true; fn() }
        }

        let ws: WebSocket
        try {
          ws = new WebSocket(url)
        } catch (e) {
          reject(buildJoinGameError(
            'Could not open the game connection. Please try again.',
            `WS construct: ${e instanceof Error ? e.message : String(e)} | url=${sanitizedUrl}`,
          ))
          return
        }
        ws.binaryType = 'arraybuffer'

        const timeout = setTimeout(() => {
          ws.close()
          settle(() => reject(buildJoinGameError(
            'Connection timed out while starting the game. Please try again.',
            `Timeout ${timeoutMs}ms | state=${ws.readyState} | url=${sanitizedUrl}`,
          )))
        }, timeoutMs)

        ws.onopen = () => {
          clearTimeout(timeout)
          const conn = new GameConnection(ws, WS_PROTOCOL_VERSION)
          this.connection = conn
          settle(() => resolve(conn))
        }

        ws.onerror = () => {
          clearTimeout(timeout)
          setTimeout(() => {
            settle(() => reject(buildJoinGameError(
              'Network error while starting the game. Please try again.',
              `WS error | state=${ws.readyState} | url=${sanitizedUrl}`,
            )))
          }, 100)
        }

        ws.onclose = (event) => {
          clearTimeout(timeout)
          settle(() => reject(buildJoinGameCloseError(
            { code: event.code, reason: event.reason || '' },
            sanitizedUrl,
          )))
        }
      })
    }

    const errors: JoinGameError[] = []
    for (let index = 0; index < WS_ENDPOINTS.length; index++) {
      const endpoint = WS_ENDPOINTS[index]
      const hasAlternateEndpoint = index < WS_ENDPOINTS.length - 1
      const timeoutMs = hasAlternateEndpoint ? 3000 : 10000
      try {
        return await attemptJoin(endpoint, timeoutMs)
      } catch (error) {
        const joinError = error instanceof JoinGameError
          ? error
          : buildJoinGameError(
            'Could not start the game. Please try again.',
            error instanceof Error ? error.message : String(error),
          )
        errors.push(joinError)
        if (!hasAlternateEndpoint || !shouldRetryJoinOnAlternateEndpoint(joinError)) {
          if (errors.length <= 1) {
            throw joinError
          }
          const combinedDebugMessage = errors.map((item, itemIndex) => `attempt#${itemIndex + 1}: ${item.debugMessage}`).join(' || ')
          throw buildJoinGameError(joinError.message, combinedDebugMessage)
        }
      }
    }

    throw buildJoinGameError(
      'Could not start the game. Please try again.',
      'No websocket endpoints available',
    )
  }

  getConnection(): GameConnection | null {
    return this.connection
  }

  getToken(): string | null {
    return this.token
  }

  async leaveGame(): Promise<void> {
    if (this.connection) {
      await this.connection.leave()
      this.connection = null
    }
  }

  clearConnection(): void {
    this.connection = null
  }
}
